import GameCommandIcon from '@/components/gameCommands/GameCommandIcon'
import commandStyles from '@/components/gameCommands/GameCommandAction.module.scss'
import { useLayoutEffect, useRef } from 'react'
import { useI18n } from '@runtime/core/i18n/i18n'
import { getHelpCharacter } from '@/features/help/characters/helpCharacters'
import { useFloatingCoachLauncher } from './useFloatingCoachLauncher'
import { useCoachLauncherPlacement } from './CoachLauncherPlacement'
import styles from './CoachPanel.module.scss'
import motion from './CoachLauncher.module.scss'

export default function CoachLauncher({
  isOpen,
  onToggle,
}: {
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useI18n()
  const placement = useCoachLauncherPlacement()
  const compact = placement?.compact ?? false
  const inMenu = placement?.inMenu ?? false
  const inGameActions = placement?.inGameActions ?? false
  const isDocked = inMenu || inGameActions
  const launcherRef = useFloatingCoachLauncher(onToggle, isOpen, inMenu, inGameActions)
  const wasOpenRef = useRef(isOpen)

  useLayoutEffect(() => {
    if (wasOpenRef.current && !isOpen && document.activeElement === document.body) {
      launcherRef.current?.focus({ preventScroll: true })
    }
    wasOpenRef.current = isOpen
  }, [isOpen, launcherRef])

  return (
    <button
      ref={launcherRef}
      className={[
        styles.launcher,
        motion.motion,
        compact && styles.compactLauncher,
        inMenu && motion.menuLauncher,
        inGameActions && commandStyles.action,
        inGameActions && motion.gameLauncher,
      ]
        .filter(Boolean)
        .join(' ')}
      type="button"
      hidden={isOpen && !inMenu}
      onClick={inMenu ? onToggle : undefined}
      aria-expanded={isOpen}
      aria-controls="coach-panel"
      data-testid="coach-launcher"
      data-presentation={inMenu ? 'menu' : compact ? 'compact' : 'badge'}
      data-game-command={inGameActions || undefined}
      data-floating={isDocked ? 'false' : undefined}
    >
      {inGameActions ? (
        <GameCommandIcon kind="coach" />
      ) : (
        <span className={styles.launcherPortrait} aria-hidden="true">
          <img src={getHelpCharacter('coach').img} alt="" draggable={false} />
        </span>
      )}
      <span className={styles.launcherLabel}>{t('coach.launcher')}</span>
      {!isDocked && (
        <span className={motion.grip} aria-hidden="true" data-testid="coach-launcher-grip" />
      )}
    </button>
  )
}
